// The ham (the site's "pekoni": a wavy strip of bacon) and the catnip (a single serrated leaf),
// modelled in code from the site's images as reference. Both are original meshes and textures.
import {
  AdditiveBlending, BoxGeometry, BufferGeometry, CanvasTexture, Color, DoubleSide, Group, Mesh,
  MeshPhysicalMaterial, Shape, ShapeGeometry, SpriteMaterial, Sprite, SRGBColorSpace, Vector2,
} from 'three';
import { laneCenterZ, posToX } from '../../data/track';

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return [c, c.getContext('2d')!];
}

function rand(seed: number): () => number {
  let s = seed >>> 0;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
}

// ---------------------------------------------------------------- bacon

let baconTexture: CanvasTexture | null = null;

/** Streaky bacon: meat and fat bands running along the strip, browned and glossy. */
function baconMap(): CanvasTexture {
  if (baconTexture) return baconTexture;
  const [c, g] = canvas(512, 128);
  const r = rand(7);
  // bands across the width (v), wavering along the length (u)
  const bands: [number, string][] = [[0, '#b8352c'], [0.18, '#efd2b4'], [0.3, '#a82a24'], [0.55, '#f2dcc2'], [0.66, '#c03a2e'], [0.88, '#ecd0b0'], [1, '#ecd0b0']];
  for (let x = 0; x < 512; x++) {
    const wob = Math.sin(x * 0.045) * 6 + Math.sin(x * 0.13 + 1) * 3;
    for (let i = 0; i < bands.length - 1; i++) {
      const y0 = bands[i]![0] * 128 + (i ? wob : 0);
      const y1 = bands[i + 1]![0] * 128 + wob;
      g.fillStyle = bands[i]![1];
      g.fillRect(x, y0, 1, y1 - y0 + 1);
    }
  }
  // marbling, speckle and browned edges
  for (let i = 0; i < 2200; i++) {
    g.fillStyle = r() < 0.5 ? 'rgba(255,235,215,0.25)' : 'rgba(120,20,15,0.22)';
    g.fillRect(r() * 512, r() * 128, 2 + r() * 8, 1 + r() * 2);
  }
  const edge = g.createLinearGradient(0, 0, 0, 128);
  edge.addColorStop(0, 'rgba(90,30,10,0.55)'); edge.addColorStop(0.12, 'rgba(90,30,10,0)');
  edge.addColorStop(0.88, 'rgba(90,30,10,0)'); edge.addColorStop(1, 'rgba(90,30,10,0.55)');
  g.fillStyle = edge; g.fillRect(0, 0, 512, 128);
  const ends = g.createLinearGradient(0, 0, 512, 0);
  ends.addColorStop(0, 'rgba(80,25,10,0.5)'); ends.addColorStop(0.04, 'rgba(80,25,10,0)');
  ends.addColorStop(0.96, 'rgba(80,25,10,0)'); ends.addColorStop(1, 'rgba(80,25,10,0.5)');
  g.fillStyle = ends; g.fillRect(0, 0, 512, 128);
  baconTexture = new CanvasTexture(c);
  baconTexture.colorSpace = SRGBColorSpace;
  baconTexture.anisotropy = 8;
  return baconTexture;
}

/** A wavy bacon strip along X, centred on the origin, lying in the XZ plane. */
export function createBaconGeometry(length = 0.14, width = 0.032, waves = 2.5, amplitude = 0.007): BufferGeometry {
  const g = new BoxGeometry(length, 0.0035, width, 64, 1, 8);
  const p = g.getAttribute('position');
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), z = p.getZ(i);
    const u = x / length;
    const y = Math.sin(u * Math.PI * 2 * waves) * amplitude + Math.sin(u * 23 + z * 90) * 0.0008;
    // the fatty edge ripples a little more than the meat
    const ripple = Math.sin(u * 40) * 0.0012 * Math.abs(z / width * 2);
    p.setY(i, p.getY(i) + y + ripple);
    p.setZ(i, z * (1 + 0.06 * Math.sin(u * 17)));
  }
  g.computeVertexNormals();
  return g;
}

let baconMaterial: MeshPhysicalMaterial | null = null;
export function createBaconMaterial(): MeshPhysicalMaterial {
  baconMaterial ??= new MeshPhysicalMaterial({
    map: baconMap(), roughness: 0.42, metalness: 0, clearcoat: 0.45, clearcoatRoughness: 0.35,
    sheen: 0.4, sheenColor: new Color('#ffd9c0'), side: DoubleSide,
  });
  return baconMaterial;
}

/** A bacon strip mesh, for the track and for a cat's mouth. */
export function createBacon(scale = 1): Mesh {
  const m = new Mesh(createBaconGeometry(0.14 * scale, 0.032 * scale, 2.5, 0.007 * scale), createBaconMaterial());
  m.castShadow = true;
  m.receiveShadow = true;
  m.name = 'bacon';
  return m;
}

// ---------------------------------------------------------------- catnip leaf

let leafTexture: CanvasTexture | null = null;

/** Leaf colour with a pale midrib and pinnate veins; the leaf runs along u, its width along v. */
function leafMap(): CanvasTexture {
  if (leafTexture) return leafTexture;
  const [c, g] = canvas(256, 256);
  const grad = g.createRadialGradient(110, 128, 10, 128, 128, 150);
  grad.addColorStop(0, '#7fbf4a'); grad.addColorStop(0.6, '#4f9a33'); grad.addColorStop(1, '#2f6e22');
  g.fillStyle = grad; g.fillRect(0, 0, 256, 256);
  const r = rand(3);
  for (let i = 0; i < 1500; i++) {
    g.fillStyle = r() < 0.5 ? 'rgba(200,255,170,0.10)' : 'rgba(20,60,10,0.12)';
    g.fillRect(r() * 256, r() * 256, 2, 2);
  }
  g.strokeStyle = 'rgba(205,240,170,0.85)';
  g.lineWidth = 3;
  g.beginPath(); g.moveTo(0, 128); g.quadraticCurveTo(128, 124, 256, 128); g.stroke();
  g.lineWidth = 1.6;
  g.strokeStyle = 'rgba(195,235,160,0.6)';
  for (let i = 1; i < 9; i++) {
    const x = i * 26;
    for (const s of [-1, 1]) {
      g.beginPath(); g.moveTo(x, 128); g.quadraticCurveTo(x + 18, 128 + s * 40, x + 34, 128 + s * 100); g.stroke();
    }
  }
  leafTexture = new CanvasTexture(c);
  leafTexture.colorSpace = SRGBColorSpace;
  return leafTexture;
}

/** A serrated ovate leaf along +X, length ~1 before scaling, folded along its midrib and arched. */
export function createLeafGeometry(length = 0.16): BufferGeometry {
  const pts: Vector2[] = [];
  const N = 120, teeth = 9;
  for (let i = 0; i <= N; i++) {
    const t = i / N; // 0 base → 1 tip → back along the other side
    const side = t <= 0.5 ? 1 : -1;
    const s = t <= 0.5 ? t * 2 : (1 - t) * 2; // 0 at the base, 1 at the tip
    // ovate outline: widest a third of the way up, rounded base, pointed tip
    const w = Math.sin(Math.PI * Math.pow(s, 0.72)) * 0.36 * (1 - 0.25 * s);
    const tooth = s > 0.06 && s < 0.97 ? Math.max(0, ((s * teeth) % 1) - 0.25) * 0.075 * (1 - s * 0.4) : 0;
    pts.push(new Vector2(s, side * (w + tooth)));
  }
  const shape = new Shape(pts);
  const g = new ShapeGeometry(shape, 1);
  const p = g.getAttribute('position');
  const uv = g.getAttribute('uv');
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i);
    uv.setXY(i, x, y / 0.9 + 0.5);
    // fold along the midrib and arch the tip down; the shape lies in XZ
    const fold = Math.abs(y) * 0.28;
    const arch = -Math.pow(x - 0.45, 2) * 0.22;
    p.setXYZ(i, (x - 0.5) * length, (fold + arch) * length, y * length);
  }
  uv.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}

export function createLeafMaterial(glow = 0.25): MeshPhysicalMaterial {
  const map = leafMap();
  return new MeshPhysicalMaterial({
    map, color: new Color('#b8d8a0'), emissiveMap: map, emissive: new Color('#7dff4a'), emissiveIntensity: glow,
    roughness: 0.55, sheen: 0.35, sheenRoughness: 0.5, sheenColor: new Color('#c8f0b0'), side: DoubleSide,
  });
}

let glowTexture: CanvasTexture | null = null;
function glowMap(): CanvasTexture {
  if (glowTexture) return glowTexture;
  const [c, g] = canvas(128, 128);
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, 'rgba(190,255,150,0.55)'); grad.addColorStop(0.35, 'rgba(120,230,90,0.18)'); grad.addColorStop(1, 'rgba(80,200,60,0)');
  g.fillStyle = grad; g.fillRect(0, 0, 128, 128);
  glowTexture = new CanvasTexture(c);
  glowTexture.colorSpace = SRGBColorSpace;
  return glowTexture;
}

/** One catnip leaf with a short stem, as an object others (the trip) can reuse. */
export function createCatnipLeaf(length = 0.16, glow = 0.25): Mesh {
  const leaf = new Mesh(createLeafGeometry(length), createLeafMaterial(glow));
  leaf.castShadow = true;
  leaf.name = 'catnip-leaf';
  const stem = new Mesh(new BoxGeometry(length * 0.18, length * 0.02, length * 0.02), leaf.material);
  stem.position.set(-length * 0.58, 0, 0);
  leaf.add(stem);
  return leaf;
}

// ---------------------------------------------------------------- the props on the track

const HOP_TIME = 0.55;

/** The ham on the track: follows the signal's ham lane and position and hops when it moves. */
export class HamProp {
  readonly group = new Group();
  private bacon = createBacon(3.4);
  private from = { x: 0, z: 0 };
  private to = { x: 0, z: 0 };
  private hop = 1;
  private placed = false;

  constructor() {
    this.group.name = 'ham';
    this.bacon.position.y = 0.05; // just above the worn lane grass (about 0.03 m) so it is not hidden in it
    this.bacon.rotation.y = 0.35;
    this.group.add(this.bacon);
    this.group.visible = false;
  }

  /** `ham` from the snapshot; `hidden` while a cat has it in its mouth. */
  update(ham: { lane: number; pos: number } | null, hidden: boolean, dt: number): void {
    if (!ham) { this.group.visible = false; this.placed = false; return; }
    const x = posToX(ham.pos), z = laneCenterZ(ham.lane);
    if (!this.placed) {
      this.from = { x, z }; this.to = { x, z }; this.hop = 1; this.placed = true;
    } else if (Math.abs(x - this.to.x) > 0.05 || Math.abs(z - this.to.z) > 0.05) {
      const cur = this.current();
      this.from = cur; this.to = { x, z }; this.hop = 0;
    }
    this.hop = Math.min(1, this.hop + dt / HOP_TIME);
    const c = this.current();
    const k = this.hop;
    const far = Math.hypot(this.to.x - this.from.x, this.to.z - this.from.z);
    this.group.position.set(c.x, Math.sin(Math.PI * k) * Math.min(1.2, 0.25 + far * 0.08), c.z);
    this.bacon.rotation.z = Math.sin(Math.PI * 2 * k) * 0.6 * (1 - k);
    this.group.visible = !hidden;
  }

  private current() {
    const k = this.hop, e = k * k * (3 - 2 * k);
    return { x: this.from.x + (this.to.x - this.from.x) * e, z: this.from.z + (this.to.z - this.from.z) * e };
  }
}

/** The catnip on the track: one leaf hovering over its lane, turning slowly with a restrained glow. */
export class CatnipProp {
  readonly group = new Group();
  readonly leaf = createCatnipLeaf(0.36, 0.22);
  private halo: Sprite;
  private t = 0;

  constructor() {
    this.group.name = 'catnip';
    this.group.add(this.leaf);
    this.halo = new Sprite(new SpriteMaterial({ map: glowMap(), blending: AdditiveBlending, depthWrite: false, transparent: true }));
    this.halo.scale.setScalar(1.1);
    this.group.add(this.halo);
    this.group.visible = false;
  }

  update(catnip: { lane: number; pos: number } | null, dt: number): void {
    if (!catnip) { this.group.visible = false; return; }
    this.t += dt;
    this.group.visible = true;
    this.group.position.set(posToX(catnip.pos), 0, laneCenterZ(catnip.lane));
    this.leaf.position.y = 0.45 + Math.sin(this.t * 1.7) * 0.06;
    this.leaf.rotation.set(0.25 + Math.sin(this.t * 1.1) * 0.12, this.t * 0.7, Math.sin(this.t * 0.9) * 0.15);
    this.halo.position.y = this.leaf.position.y;
    (this.leaf.material as MeshPhysicalMaterial).emissiveIntensity = 0.2 + 0.08 * Math.sin(this.t * 2.3);
    this.halo.material.opacity = 0.7 + 0.2 * Math.sin(this.t * 2.3);
  }
}
